import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { of, timer } from 'rxjs';
import { catchError, debounce, filter, map, switchMap } from 'rxjs/operators';
import { RetailersService } from '@emi/shared/data-access';
import * as retailerActions from './retailer-actions';
import * as dataExtractionActions from '../extraction/extraction-actions';
import { HttpErrorResponse } from '@angular/common/http';
import { select, Store } from '@ngrx/store';
import { RetailerState } from './retailer-reducer';
import { getRetailerState } from './retailer-selectors';
import { Filters, RetailerDto } from '@emi/shared/models';
import { DataExtractionState } from '../extraction/extraction-reducer';
import { getSelectedFilters } from '../extraction/extraction-selectors';
import { createFilterDto } from '@emi/shared/utils';
import { selectSkuSearch, SkuSearchState } from '@emi/shared/state/sku-search';

@Injectable()
export class RetailerEffects {
    private currentState: RetailerState;
    private currentFilters: Filters;
    private previousFilters: Filters;
    private searchTermState: string;
    private previousSearchTermState: string;
    private includeSearchPhrase: boolean | undefined;

    constructor(
        private actions$: Actions,
        private retailersService: RetailersService,
        private retailerStore: Store<RetailerState>,
        private store: Store<DataExtractionState>,
        private skuSearchStore: Store<SkuSearchState>
    ) {
        this.retailerStore.pipe(select(getRetailerState)).subscribe((x) => (this.currentState = x));
        this.skuSearchStore.pipe(select(selectSkuSearch)).subscribe((skuSearchState) => (this.searchTermState = skuSearchState.searchTerm, this.includeSearchPhrase = skuSearchState.includeSearchPhrase));
    }

    getRetailers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(retailerActions.getRetailers),
            map(() => {
                return retailerActions.isLoadingRetailers();
            })
        )
    );

    getRetailersNextPage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(retailerActions.getNextPageRetailers),
            switchMap(() => {
                return this.retailersService
                    .getRetailers(
                        createFilterDto({
                            filterState: this.currentFilters,
                            pageId: this.currentState.metadata?.nextPageId,
                            searchTerm: this.includeSearchPhrase ? this.searchTermState : undefined,
                            searchText: this.currentState.searchText ?? undefined
                        })
                    )
                    .pipe(
                        map((data: RetailerDto) => {
                            this.previousFilters = this.currentFilters;
                            const newPage = data.results;

                            return retailerActions.updateRetailersSelectionSuccess({ retailers: newPage, metadata: data.metadata });
                        }),
                        catchError((error: HttpErrorResponse) => {
                            return of(retailerActions.getRetailersFailure({ error }));
                        })
                    );
            })
        )
    );

    amendSelected$ = createEffect(() =>
        this.actions$.pipe(
            ofType(retailerActions.amendRetailerSelections),
            switchMap(() => {
                this.store.pipe(select(getSelectedFilters)).subscribe((filtersState) => (this.currentFilters = filtersState));

                return this.retailersService
                    .getRetailers(
                        createFilterDto({
                            filterState: this.currentFilters,
                            searchTerm: this.includeSearchPhrase ? this.searchTermState : undefined,
                            pageId: this.currentState.metadata?.nextPageId,
                        })
                    )
                    .pipe(
                        map((retailersResponse) => {
                            const amendedRetailers = this.currentFilters.retailers.filter(
                                (r) => !!retailersResponse.results.find((retailer) => retailer.id == r.id)
                            );
                            return dataExtractionActions.addRetailerSelection({ retailers: amendedRetailers });
                        }),
                        catchError((error: HttpErrorResponse) => {
                            if (error.status == 404) {
                                return of(dataExtractionActions.addRetailerSelection({ retailers: [] }));
                            }
                            return of(retailerActions.getRetailersFailure({ error }));
                        })
                    );
            })
        )
    );

    fetchRetailers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(retailerActions.fetchRetailers),
            switchMap(() => {
                this.store.pipe(select(getSelectedFilters)).subscribe((filtersState) => (this.currentFilters = filtersState));
                if (this.currentFilters.retailers.length > 0) {
                    if (this.currentFilters === this.previousFilters && this.searchTermState === this.previousSearchTermState) {
                        return of(
                            retailerActions.getRetailersSuccess({
                                retailers: this.currentState.retailers,
                                metadata: this.currentState.metadata,
                                searchText: this.currentState.searchText ?? '',
                            })
                        );
                    }
                }
                return this.retailersService
                    .getRetailers(createFilterDto({ filterState: this.currentFilters, searchTerm: this.includeSearchPhrase? this.searchTermState?? undefined : '' }))
                    .pipe(
                        map((data: RetailerDto) => {
                            this.previousFilters = this.currentFilters;
                            this.previousSearchTermState = this.searchTermState;
                            return retailerActions.getRetailersSuccess({
                                retailers: data.results,
                                metadata: data.metadata,
                                searchText: this.currentState.searchText ?? '',
                            });
                        }),
                        catchError((error: HttpErrorResponse) => {
                            return of(retailerActions.getRetailersFailure({ error }));
                        })
                    );
            })
        )
    );

    searchRetailers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(retailerActions.searchRetailers),
            debounce((action) => timer(action.delay)),
            filter((action) => action.searchText !== this.currentState.searchText),
            concatLatestFrom(() => this.store.select(getSelectedFilters)),
            switchMap(([action, currentFilters]) => {
                return this.retailersService
                    .getRetailers(
                        createFilterDto({
                          filterState: currentFilters,
                          searchText: action.searchText,
                          searchTerm: this.includeSearchPhrase ? this.searchTermState : undefined,
                        })
                    )
                    .pipe(
                        map((data: RetailerDto) => {
                            this.previousFilters = currentFilters;
                            return retailerActions.getRetailersSuccess({
                                retailers: data.results,
                                metadata: data.metadata,
                                searchText: action.searchText,
                            });
                        }),
                        catchError((error: HttpErrorResponse) => {
                            return of(retailerActions.getRetailersFailure({ error }));
                        })
                    );
            })
        )
    );

    setIsLoading$ = createEffect(() =>
        this.actions$.pipe(
            ofType(retailerActions.isLoadingRetailers),
            map(() => {
                return retailerActions.fetchRetailers();
            })
        )
    );
}
